import { Map, View, Feature } from 'ol';
import { Style, Fill, Stroke, Circle, Icon, Text, RegularShape } from 'ol/style';
import { Point, MultiPoint, LineString, Geometry, MultiPolygon, Polygon, MultiLineString } from 'ol/geom';
import { RouteMapStyles } from './MapStyleConfig/RouteMapStyles';
import { LandMapStyles } from './MapStyleConfig/LandMapStyles';
import { TROMapStyles } from './MapStyleConfig/TROMapStyles';
import { MaintMapStyles } from './MapStyleConfig/MaintMapStyles';
import { InfraMapStyles } from './MapStyleConfig/InfraMapStyles';
import { HighlightMapStyles } from './MapStyleConfig/HighlightMapStyles';
import { MapConfigModel, MapLayerGroupType } from '../Models/MapConfigModels';
import { LocationMapStyles } from './MapStyleConfig/LocationMapStyles';
import GeometryType from 'ol/geom/GeometryType';
import { LegalRouteMapStyles } from './MapStyleConfig/LegalRouteMapStyles';
import { LegalFeatureMapStyles } from './MapStyleConfig/LegalFeatureMapStyles';

export class MapStyles {

    public MaintMapStyles: MaintMapStyles = new MaintMapStyles();
    public InfraMapStyles: InfraMapStyles = new InfraMapStyles();
    public LandMapStyles: LandMapStyles = new LandMapStyles();
    public RouteMapStyles: RouteMapStyles = new RouteMapStyles();
    public LegalRouteMapStyles: LegalRouteMapStyles = new LegalRouteMapStyles();
    public LegalFeatureMapStyles: LegalFeatureMapStyles = new LegalFeatureMapStyles();

    public init(mapConfigModel: MapConfigModel) {
        this.MaintMapStyles.init(mapConfigModel.coreLayers.filter(f => f.mapLayerGroupType == MapLayerGroupType.Maint || f.mapLayerGroupType == MapLayerGroupType.MyMaint));
        this.InfraMapStyles.init(mapConfigModel.coreLayers.filter(f => f.mapLayerGroupType == MapLayerGroupType.Infra));
        this.LandMapStyles.init(mapConfigModel.coreLayers.filter(f => f.mapLayerGroupType == MapLayerGroupType.Land));
        this.RouteMapStyles.init(mapConfigModel.coreLayers.filter(f => f.mapLayerGroupType == MapLayerGroupType.Route));
        this.LegalRouteMapStyles.init(mapConfigModel.coreLayers.filter(f => f.mapLayerGroupType == MapLayerGroupType.LegalRoute));
        this.LegalFeatureMapStyles.init(mapConfigModel.coreLayers.filter(f => f.mapLayerGroupType == MapLayerGroupType.LegalFeature));
    }


    // ----------------------------------------------------------- //
    // STYLE FUNCTIONS

    // ANNOTATION //
    static annotationStyleFunction = (feature: Feature, resolution, self): Style[] => {
        var styles: Array<Style> = [];
   
        // Create the main feature styles
        var style = new Style({
            image: new Circle({ radius: 10, fill: new Fill({ color: 'rgba(0, 0, 0, 0.1)' }) }),
            stroke: MapStyles.createStrokeStyle(feature, resolution),
            fill: MapStyles.createFillStyle(feature, resolution),
            text: MapStyles.createTextStyle(feature, resolution)
        });
        styles.push(style);

        // This will optionally add an arrow endcap to a line is it is specified
        styles = styles.concat(MapStyles.createEndLineArrow(feature));

        return styles;
    }

    static createTextStyle = (feature: Feature, resolution): Text => {
        var align = "Center";
        var baseline = "Middle";
        var size = "14px";
        var height = 1;
        var offsetX = 0;
        var offsetY = 0;
        var weight = "Bold";
        var placement = "Point";
        var maxAngle = "0.7853981633974483";
        var overflow = "False";
        var rotation = 0;
        var fillColor = "#aa3300";
        var outlineColor = "#ffffff";
        var outlineWidth = 0;
        var fontStr = "Arial";
        var backgroundFill = null;
        var backgroundStroke = null;
        var padding = null;

        // Check styles
        var styleoverride = feature.get('Style');
        if (styleoverride instanceof Object == true && styleoverride.text instanceof Object == true) {
            try {
                if (styleoverride.text.size != null) size = styleoverride.text.size + "px";
                if (styleoverride.text.fillColor != null) fillColor = styleoverride.text.fillColor;
                if (styleoverride.text.outlineColor != null) outlineColor = styleoverride.text.outlineColor;
                if (styleoverride.text.outlineWidth != null) outlineWidth = styleoverride.text.outlineWidth;
                if (styleoverride.text.weight != null) weight = styleoverride.text.weight;
                if (styleoverride.text.font != null) fontStr = styleoverride.text.font;
                if (styleoverride.text.backgroundFillColor != null) backgroundFill = new Fill({ color: styleoverride.text.backgroundFillColor });
                if (styleoverride.text.backgroundStrokeColor != null) backgroundStroke = new Stroke({ color: styleoverride.text.backgroundStrokeColor });
                if (styleoverride.text.padding != null) padding = styleoverride.text.padding;
                if (styleoverride.text.placement != null) placement = styleoverride.text.placement;
                if (styleoverride.text.offsetY != null) offsetY = styleoverride.text.offsetY;
            }
            catch (e) {
                console.log("MapStyles.ts: Annotation failed to parse style: " + feature.get('AnnotatinoUID') + " " + styleoverride);
                console.log(e);
                // Ignore and move on
            }
        }

        var getText = function (feature, resolution) {
            var type = "wrap";
            var maxResolution = "1200";
            var text = feature.get('Text');

            if (resolution > maxResolution) {
                text = '';
            } else if (type == 'hide') {
                text = '';
            } else if (type == 'shorten') {
                text = text.trunc(12);
            } else if (
                type == 'wrap'
            ) {
                text = stringDivider(text, 16, '\n');
            }

            return text;
        };

        function stringDivider(str, width, spaceReplacer) {
            if (str.length > width) {
                var p = width;
                while (p > 0 && str[p] != ' ' && str[p] != '-') {
                    p--;
                }
                if (p > 0) {
                    var left;
                    if (str.substring(p, p + 1) == '-') {
                        left = str.substring(0, p + 1);
                    } else {
                        left = str.substring(0, p);
                    }
                    var right = str.substring(p + 1);
                    return left + spaceReplacer + stringDivider(right, width, spaceReplacer);
                }
            }
            return str;
        }

        return new Text({
            font: weight + ' ' + size + '/' + height + ' ' + fontStr,
            text: getText(feature, resolution),
            fill: new Fill({ color: fillColor }),
            stroke: new Stroke({ color: outlineColor, width: outlineWidth }),
            backgroundFill: backgroundFill,
            backgroundStroke: backgroundStroke,
            padding: padding,
            placement: placement,
            offsetY: offsetY
        });

        //return new Text({
        //    textAlign: align,
        //    textBaseline: baseline,
        //    font: font,
        //    text: getText(feature, resolution),
        //    fill: new Fill({ color: fillColor }),
        //    stroke: new Stroke({ color: outlineColor, width: outlineWidth }),
        //    offsetX: offsetX,
        //    offsetY: offsetY,
        //    placement: placement,
        //    maxAngle: maxAngle,
        //    overflow: overflow,
        //    rotation: rotation,
        //});

    }

    static createStrokeStyle = (feature: Feature, resolution): Stroke => {
        var strokeStyle = new Stroke({ color: 'rgba(255, 50, 50, 0.8)', width: 2 });

        try {
            var styleoverride = feature.get('Style');
            if (styleoverride instanceof Object == true && styleoverride.stroke instanceof Object == true) {
                if (styleoverride.stroke.color != null) strokeStyle.setColor(styleoverride.stroke.color);
                if (styleoverride.stroke.width != null) strokeStyle.setWidth(styleoverride.stroke.width);

                if (styleoverride.stroke.lineDash != null) {
                    if (styleoverride.stroke.lineDash =="dash") {
                        strokeStyle.setLineDash([6,6]);
                    }
                    else if (styleoverride.stroke.lineDash == "dot") {
                        strokeStyle.setLineDash([1,8]);
                    }
                }
            }
        }
        catch (e) {
            console.log("MapStyles.createFillStyle: Failed to parse style: " + feature.get('Style'));
        }
        return strokeStyle;
    }

    static createFillStyle = (feature: Feature, resolution): Fill => {
        var fillStyle = new Fill({ color: 'rgba(255, 155, 155, 0.8)' });
        try {
            var styleoverride = feature.get('Style');
            if (styleoverride instanceof Object == true && styleoverride.fill instanceof Object == true) {
                if (styleoverride.fill.color != null) fillStyle.setColor(styleoverride.fill.color);
            }
        }
        catch (e) {
            console.log("MapStyles.createFillStyle: Failed to parse style: " + feature.get('Style'));
        }
        return fillStyle;
    }

    static createEndLineArrow = (feature: Feature): Array<Style> => {
        var styles: Array<Style> = [];
        var geometry = feature.getGeometry();
        if (geometry instanceof LineString == false) return styles; // Only do add the endCap if this is a line string

        var styleoverride = feature.get('Style');
        if (styleoverride instanceof Object == true && styleoverride.stroke instanceof Object == true) {
            if (styleoverride.stroke.endCap == "arrow") {
                var color = styleoverride.stroke.color == null ? 'rgba(255, 50, 50, 0.8)' : styleoverride.stroke.color;

                // Get the last segment start and end
                var c = (feature.getGeometry() as any).flatCoordinates;
                var endP = [c[c.length - 2], c[c.length - 1]];
                var startP = [c[c.length - 4], c[c.length - 3]];

                // Draw an arrow on the last segment
                var dx = endP[0] - startP[0];
                var dy = endP[1] - startP[1];
                var rotation = Math.atan2(dy, dx);

                styles.push(new Style({
                    geometry: new Point(endP),
                    image: new RegularShape({
                        fill: new Fill({ color: color }),
                        points: 3,
                        radius: 14,
                        rotation: -rotation,
                        angle: Math.PI / 2 // rotate 90°
                    })
                }));
            }
        }

        return styles;
    }

    // ----------------------------------------------------------- //
    // TRO //
    static troStyleFunction = (feature: Feature, resolution, self): Style[] => {
        const level = feature.get('TroFeatureType');
        var styleCache = TROMapStyles.troStyleCache.find(c => c.attributeValue == level);
        if (!level || styleCache==null) {
            return [TROMapStyles.troStyleCache.find(c => c.attributeValue == 'Default').style];
        }
        else {
            return [styleCache.style];
        }
    }

    // ----------------------------------------------------------- //
    // HIGHLIGHT //
    static highlightStyleFunction = (feature: Feature, resolution, self): Style[] => {
        return [HighlightMapStyles.highlightStyleCache['Default']];
    }

    // ----------------------------------------------------------- //
    // LOCATION //
    static locationStyleFunction = (feature: Feature, resolution, self): Style[] => {
        return [LocationMapStyles.locationStyleCache['Default']];
    }

    // ----------------------------------------------------------- //
    // SELECTED LAYERS //
    static selectedFeaturesStyleFunction = (feature: Feature, resolution, self): Style[] => {
        return [HighlightMapStyles.selectedFeaturesStyleCache['Default']];
    }

    // ----------------------------------------------------------- //
    // EDIT //
    // Used by EDIT tools //
    static editStyleFunction = (feature: Feature, resolution, self): Style[] => {
        var styles = [
            /* We are using two different styles for the polygons:
             *  - The first style is for the polygons themselves.
             *  - The second style is to draw the vertices of the polygons.
             *    In a custom `geometry` function the vertices of a polygon are
             *    returned as `MultiPoint` geometry, which will be used to render
             *    the style.
             */
            HighlightMapStyles.editPolygonStyle,
            new Style({
                image: new Circle({
                    radius: 5,
                    fill: new Fill({
                        color: 'red'
                    })
                }),
                geometry: function (feature) {
                    // return the coordinates of the geometry as a MultiPoint, if this is a polygon then return the first ring
                    //var coordinates: Array<any> = (feature.getGeometry() as any).getCoordinates();
                    //if (feature.getGeometry().getType() == "Polygon" || feature.getGeometry().getType() == "MultiPolygon") {
                    //    coordinates = [];
                    //    (feature.getGeometry() as any).getCoordinates().forEach(
                    //        c => { coordinates = coordinates.concat(c); }
                    //    );
                    //}

                    var coordinates = [];

                    if (feature.getGeometry().getType() == GeometryType.POINT) coordinates = (feature.getGeometry() as Point).getFlatCoordinates();
                    else if (feature.getGeometry().getType() == GeometryType.MULTI_POINT) coordinates = (feature.getGeometry() as MultiPoint).getFlatCoordinates();
                    else if (feature.getGeometry().getType() == GeometryType.LINEAR_RING) coordinates = (feature.getGeometry() as LineString).getFlatCoordinates();
                    else if (feature.getGeometry().getType() == GeometryType.MULTI_LINE_STRING) coordinates = (feature.getGeometry() as MultiLineString).getFlatCoordinates();
                    else if (feature.getGeometry().getType() == GeometryType.POLYGON) coordinates = (feature.getGeometry() as Polygon).getFlatCoordinates();
                    else if (feature.getGeometry().getType() == GeometryType.MULTI_POLYGON) coordinates = (feature.getGeometry() as MultiPolygon).getFlatCoordinates();

                    var c2 = [];
                    for (var i = 0; i < coordinates.length; i = i + 2) {
                        c2.push([coordinates[i], coordinates[i+1]]);
                    }

                    return new MultiPoint(c2);
                }
            })
        ];

        return styles;
    }

}




